import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ApiServiceBase } from '@api/abstracts';
import { environment } from '@environments/environment';
import { CacheService } from '@services/cache';
import { LogsService } from '@services/logs';
import { UrlService } from '@services/url';
import { RoutesPath } from '@shared/constants';
import { CustomError } from '@shared/models';
import { Page } from '@shared/models/page';
import { BehaviorSubject, debounceTime, Observable, of, ReplaySubject, Subject, Subscription, switchMap } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class GlobalService extends ApiServiceBase<Page> implements OnDestroy {
  closeIframeCallbackEvent$ = new Subject<void>();
  forceReloadData$ = this.closeIframeCallbackEvent$.pipe(
    debounceTime(200),
    switchMap(() => this.forceReloadData())
  );
  private readonly withBreadcrumb$ = new BehaviorSubject<boolean>(true);
  private readonly urlService = inject(UrlService);
  private readonly router = inject(Router);
  private readonly cacheService = inject(CacheService);
  private readonly logsService = inject(LogsService);
  private readonly data$ = new ReplaySubject<Page>();
  private forceReloadDataSub: Subscription;

  constructor() {
    super('');
    this.forceReloadDataSub = this.forceReloadData$.subscribe();
  }

  ngOnDestroy() {
    this.forceReloadDataSub.unsubscribe();
  }

  getData$(): ReplaySubject<Page> {
    return this.data$;
  }

  setData(data: Page): void {
    this.data$.next(data);
  }

  getWithBreadcrumb$(): Subject<boolean> {
    return this.withBreadcrumb$;
  }

  setWithBreadcrumb(withBreadcrumb: boolean): void {
    this.withBreadcrumb$.next(withBreadcrumb);
  }

  forceReloadData(): Observable<Page | undefined> {
    return this.urlService.getUrlDetails$().pipe(
      mergeMap((details) => {
        if (details.pathname === `/${RoutesPath.HOME}`) {
          details.pathname = '/homepage';
        }
        const keyToRemove = `${environment.API.endpoint.default}/slug?slug=${details.pathname}`;
        this.cacheService
          .remove(keyToRemove)
          .catch((error: unknown) =>
            this.logsService.warn(
              'GlobalService',
              'Failed to evict cached slug before force reload',
              keyToRemove,
              error
            )
          );

        let request = this.getSlug(details.pathname, {}, { cacheExpirationTime: 600 });

        if (environment.isPrefetchCmsEnabled) {
          request = this.readPrefetch(details.pathname, { cacheExpirationTime: 600 });
        }
        return request.pipe(
          tap((data) => this.setData(data)),
          catchError((error: CustomError) => {
            this.logsService.error('GlobalService', 'forceReloadData failed', { pathname: details.pathname }, error);
            if (error.status === 404) {
              const data = error.error as Page;
              if ('block' in data) {
                return of(data);
              }
            }
            void this.router.navigate([`${RoutesPath.HOME}`]);
            return of(undefined);
          })
        );
      })
    );
  }
}
